/**
 * Video Banner Section
 * Full-width video embed with overlay and CTA
 * Supports YouTube, Vimeo, and self-hosted videos
 */

import React, { useState } from 'react';
import VideoPlayer from './VideoPlayer';
import './VideoBanner.css';

function VideoBanner({ settings = {}, content = {}, globalStyles = {}, store }) {
  const {
    height = '500px',
    autoplay = true,
    muted = true,
    loop = true,
    showControls = false,
    overlayOpacity = 0.3,
    objectFit = 'cover',
    textAlign = 'center',
    verticalAlign = 'center',
    // Per-section style overrides
    overlayColor = '#000000',
    titleColor = '#ffffff',
    subtitleColor = '#e5e5e5',
    buttonBgColor,
    buttonTextColor = '#ffffff',
    buttonStyle = 'filled',
    buttonRadius = '8px',
  } = settings;

  const {
    title = '',
    subtitle = '',
    videoUrl = '',
    posterImage = '',
    buttonText = '',
    buttonLink = '',
  } = content;

  const [isPlaying, setIsPlaying] = useState(autoplay);

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  // Use section-level settings first, then fall back to global styles
  const primaryColor = buttonBgColor || colors.primary || '#2563eb';
  const headingFont = fonts.heading || 'inherit';
  const bodyFont = fonts.body || 'inherit';

  const hasContent = title || subtitle || buttonText;

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  // Alignment classes for content positioning
  const alignClass = `vb-content--align-${textAlign}`;
  const valignClass = `vb-content--valign-${verticalAlign}`;

  // Compute button styles based on buttonStyle setting
  const getButtonStyles = () => {
    const base = {
      borderRadius: buttonRadius,
      fontFamily: bodyFont,
    };
    switch (buttonStyle) {
      case 'outline':
        return { ...base, backgroundColor: 'transparent', color: primaryColor, border: `2px solid ${primaryColor}` };
      case 'ghost':
        return { ...base, backgroundColor: 'transparent', color: buttonTextColor, border: 'none' };
      default: // filled
        return { ...base, backgroundColor: primaryColor, color: buttonTextColor, border: 'none' };
    }
  };

  return (
    <section
      className="vb-container"
      style={{
        '--vb-height': height,
        '--vb-overlay-opacity': overlayOpacity,
        '--vb-overlay-color': overlayColor,
        '--vb-primary': primaryColor,
        '--vb-heading-font': headingFont,
        '--vb-body-font': bodyFont,
        '--vb-btn-radius': buttonRadius,
        '--vb-title-color': titleColor,
        '--vb-subtitle-color': subtitleColor,
        backgroundColor: colors.background || '#1a1a2e',
      }}
    >
      {/* Video Background */}
      <div className="vb-video-wrapper">
        {isPlaying ? (
          <VideoPlayer
            url={videoUrl}
            autoplay={autoplay}
            muted={muted}
            loop={loop}
            controls={showControls}
            poster={posterImage}
            className="vb-video"
            style={{ objectFit }}
          />
        ) : posterImage ? (
          <img
            src={posterImage}
            alt={title || 'Video thumbnail'}
            className="vb-poster"
          />
        ) : null}
      </div>

      {/* Overlay */}
      <div className="vb-overlay" />

      {/* Content */}
      {(hasContent || (!isPlaying && videoUrl)) && (
        <div className={`vb-content ${alignClass} ${valignClass}`}>
          <div className="vb-content__inner">
            {title && (
              <h1 className="vb-title" style={{ color: titleColor, fontFamily: headingFont }}>
                {title}
              </h1>
            )}

            {subtitle && (
              <p className="vb-subtitle" style={{ color: subtitleColor, fontFamily: bodyFont }}>
                {subtitle}
              </p>
            )}

            {/* Play button when not autoplaying */}
            {!isPlaying && videoUrl && (
              <button
                className="vb-play-btn"
                onClick={handlePlayClick}
                aria-label="Play video"
              >
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="32" r="31" stroke="white" strokeWidth="2" />
                  <path d="M26 20l20 12-20 12V20z" fill="white" />
                </svg>
              </button>
            )}

            {/* CTA Button */}
            {buttonText && isPlaying && (
              <a
                href={buttonLink || '#'}
                className="vb-button"
                style={getButtonStyles()}
              >
                {buttonText}
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default React.memo(VideoBanner);
